import { Translation } from "./hooks/useTranslations";

export const getName = (
  item: {
    name: string;
    nameIt?: string | null;
    nameEs?: string | null;
    nameFr?: string | null;
    nameDe?: string | null;
  },
  locale: string | null,
): string => {
  const lang = locale?.slice(0, 2).toLowerCase();
  switch (lang) {
    case "it":
      return item.nameIt || item.name;
    case "es":
      return item.nameEs || item.name;
    case "fr":
      return item.nameFr || item.name;
    case "de":
      return item.nameDe || item.name;
    default:
      return item.name;
  }
};

export const formatDate = (value: string, locale?: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  return new Date(value).toLocaleDateString(locale, options);
};

// A UTC day ('YYYY-MM-DD'), e.g. of the usage statistics, with its weekday: the same day in every time zone.
export const formatUtcDay = (day: string, locale?: string): string =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString(locale, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

export const formatTimeStamp = (value: string, locale?: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };
  return new Date(value).toLocaleDateString(locale, options);
};

export const formatBoolean = (translations: Translation, value?: boolean): string =>
  value ? translations.common.yes : translations.common.no;

export const formatCurrency = (value: number, currency: string, language: string): string =>
  new Intl.NumberFormat(language, {
    style: "currency",
    currency: currency,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(value ?? 0);

const numberFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});
export const formatWeight = (value: number, unitOfMeasurement: "kg" | "lb"): string =>
  `${numberFormat.format((value ?? 0) / (unitOfMeasurement === "lb" ? 2.2046 : 1))} ${unitOfMeasurement}`;

export const generateSecret = (length: number) => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmopqrstuvwxyz0123456789!()_=";
  let result = "";
  let counter = 0;
  while (counter < length) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
    counter++;
  }
  return result;
};

export const chunkArray = <T>(arr: T[], size: number) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, i * size + size));

// returns a timestamp rounded down to the minute
export const getMinutesRoundedTimestamp = (): number => {
  const now = new Date();
  now.setSeconds(0, 0); // Set seconds and milliseconds to 0
  return Math.floor(now.getTime() / 1000); // Get UNIX timestamp in seconds
};

export const areArraysEqual = <T extends string | number>(a: T[], b: T[]): boolean =>
  a.length === b.length && a.every((val, i) => val === b[i]);

export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const isRothoblaasEmail = (email?: string): boolean =>
  !!email && email.trim().toLowerCase().endsWith("@rothoblaas.com");

/**
 * Mirrors the backend's CanManageModuleCalculations: whether this user may create, edit, or
 * duplicate calculations in a module with this visibility. Used only to hide/disable UI - the
 * backend enforces this independently on every create/update/duplicate call.
 */
export const canManageModuleCalculations = (visibility?: string, email?: string): boolean => {
  if (visibility === "public") return true;
  if (visibility === "internal") return isRothoblaasEmail(email);
  return false;
};

// Loads a static asset (e.g. from /public) as base64 PNG data, without the "data:image/png;base64," prefix,
// as the server-rendered PDF reports take their images. Returns null on failure so a missing/broken asset
// doesn't prevent the rest of the report from generating.
export const loadPublicImageAsBase64 = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }

    const bytes = new Uint8Array(await response.arrayBuffer());
    let binary = "";
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }
    return btoa(binary);
  } catch {
    return null;
  }
};

// Loads a static SVG asset (e.g. from /public) as base64 PNG data rendered at width pixels, its height following
// the SVG's viewBox, without the "data:image/png;base64," prefix - the server-rendered PDF reports only take
// PNGs. Returns null on failure, as loadPublicImageAsBase64.
export const loadPublicSvgAsPngBase64 = async (url: string, width: number): Promise<string | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }

    // Sized from its viewBox: not every browser draws an SVG without its own width and height on a canvas.
    const svg = new DOMParser().parseFromString(await response.text(), "image/svg+xml").documentElement;
    const [, , viewBoxWidth, viewBoxHeight] = (svg.getAttribute("viewBox") ?? "").split(/[\s,]+/).map(Number);
    if (!viewBoxWidth || !viewBoxHeight) {
      return null;
    }
    const height = Math.round((width * viewBoxHeight) / viewBoxWidth);
    svg.setAttribute("width", String(width));
    svg.setAttribute("height", String(height));

    const objectUrl = URL.createObjectURL(
      new Blob([new XMLSerializer().serializeToString(svg)], { type: "image/svg+xml" }),
    );
    try {
      const image = new Image();
      image.src = objectUrl;
      await image.decode();

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) {
        return null;
      }
      context.drawImage(image, 0, 0, width, height);
      return canvas.toDataURL("image/png").replace(/^data:image\/png;base64,/, "");
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    return null;
  }
};
